// Per-project operational settings — fed by the gear-icon modal in the
// topbar. We build the nested URL explicitly here instead of going through
// the api() rewrite list because `/api/settings/...` still hosts the
// legacy Retell-live endpoints (retell-voice, agent-prompts, agent-diagnostic)
// that don't move to per-project until Phase 4.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, getActiveApiScope } from "@/lib/api";

export interface ProjectSettings {
  projectId: string;
  defaultAppointmentMin: number;
  agentGender: "MALE" | "FEMALE";
  agentVoiceId: string;
  maxRetries: number;
  retryCooldownHours: number;
  campaignDefaultMode: "LIVE" | "SIMULATION";
  callHoursStart: string;
  callHoursEnd: string;
  callDays: number[];
  recordingDisclosureMsg: string;
  callerFirstName: string;
  callerLastName: string;
  callerBusinessName: string;
  agentPersonaName: string;
  dryRunEnabled: boolean;
  dryRunPhoneNumber: string;
  dryRunEmail: string;
  inboundEnabled: boolean;
  // Phase 4 telephony bindings.
  retellAgentId: string | null;
  retellInboundAgentId: string | null;
  telnyxInboundNumber: string | null;
  /** Server returns this boolean — the encrypted key never crosses the wire. */
  hasTelnyxApiKey: boolean;
  updatedAt: string;
}

/** PATCH-only shape — telnyxApiKey is plaintext (server encrypts). */
export interface ProjectSettingsPatch
  extends Partial<Omit<ProjectSettings, "hasTelnyxApiKey" | "updatedAt" | "projectId">> {
  telnyxApiKey?: string | null;
}

function projectSettingsPath(): string | null {
  const scope = getActiveApiScope();
  if (!scope) return null;
  return `/api/workspaces/${scope.workspaceId}/projects/${scope.projectId}/settings`;
}

export function useProjectSettings(opts: { enabled?: boolean } = {}) {
  return useQuery<ProjectSettings>({
    queryKey: ["projectSettings"],
    queryFn: () => {
      const path = projectSettingsPath();
      if (!path) throw new Error("No active project");
      return api<ProjectSettings>(path);
    },
    staleTime: 60_000,
    enabled: opts.enabled ?? true,
  });
}

export function useUpdateProjectSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: ProjectSettingsPatch) => {
      const path = projectSettingsPath();
      if (!path) throw new Error("No active project");
      return api<ProjectSettings>(path, {
        method: "PATCH",
        body: JSON.stringify(patch),
      });
    },
    onSuccess: (data) => {
      qc.setQueryData(["projectSettings"], data);
      // A change to dryRunEnabled affects the DryRunBanner; nudge.
      qc.invalidateQueries({ queryKey: ["campaigns"] });
    },
  });
}
